import React, { useRef } from 'react';
import useChatStore from '../../stores/useChatStore';
import FlerpieSilhouette from './FlerpieSilhouette';
import Tooltip from '../ui/Tooltip';
import { FOCUS, MS, PRESS, STATE, usePresence } from '../ui/motion';
import { FLERPIE_PANEL_ID } from './flerpieDock';

interface LauncherState {
  dockReady: boolean;
  isOpen: boolean;
  pendingNotes: number;
  isStreaming: boolean;
  toggleOpen: () => void;
}

/**
 * Flerpie's silhouette in the nav, beside the bell, on desktop. Opens and closes the docked
 * panel. His emotions live in the panel; here he only glows when it matters.
 * Renders nothing until FlerpieChatBot says the dock is live on this page.
 */
const FlerpieLauncher: React.FC<{ className?: string }> = ({ className = '' }) => {
  const dockReady = useChatStore((s: LauncherState) => s.dockReady);
  const isOpen = useChatStore((s: LauncherState) => s.isOpen);
  const pendingNotes = useChatStore((s: LauncherState) => s.pendingNotes);
  const isStreaming = useChatStore((s: LauncherState) => s.isStreaming);
  const toggleOpen = useChatStore((s: LauncherState) => s.toggleOpen);
  const { mounted, open } = usePresence(dockReady, MS.exit);
  // The badge fades out showing the count it had, not an empty pill.
  const lastCount = useRef(pendingNotes);
  if (pendingNotes > 0) lastCount.current = pendingNotes;

  if (!mounted) return null;

  const waiting = pendingNotes > 0 && !isOpen;
  const notes = `${pendingNotes} ${pendingNotes === 1 ? 'note' : 'notes'} waiting`;
  const action = isOpen ? 'Close Flerpie' : 'Open Flerpie';
  // He lights up while the panel is open or he is answering, and breathes only while answering.
  const lit = isOpen || isStreaming;

  return (
    <Tooltip label={action} note={waiting ? notes : undefined} side="bottom">
      <button
        type="button"
        data-flerpie-launcher=""
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isOpen) {
            e.preventDefault();
            toggleOpen();
          }
        }}
        aria-label={waiting ? `${action}, ${notes}` : action}
        aria-expanded={isOpen}
        aria-controls={isOpen ? FLERPIE_PANEL_ID : undefined}
        className={`${STATE} ${PRESS} ${FOCUS} relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-[opacity,color,transform,--np-layer] duration-standard ease-emphasized ${
          open ? 'opacity-100' : 'opacity-0'
        } ${lit ? 'text-primary' : 'text-on-surface-variant hover:text-on-surface'} ${className}`}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-1.5 rounded-full bg-primary blur-md transition-opacity duration-standard ease-emphasized ${
            isStreaming ? 'opacity-30 motion-safe:animate-glowBreathe' : 'opacity-0'
          }`}
        />
        <FlerpieSilhouette className={`relative h-[22px] w-[22px] ${lit ? 'drop-shadow-glow' : ''}`} />
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none tabular-nums text-on-primary ring-2 ring-surface transition-[opacity,transform] duration-standard ease-emphasized motion-reduce:scale-100 ${
            waiting ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
          }`}
        >
          {lastCount.current}
        </span>
      </button>
    </Tooltip>
  );
};

export default FlerpieLauncher;
